import { BlackjackRoundCreator } from './blackjack-round-creator';
import { Subject } from 'rxjs';
import {
  BlackjackGameLogic,
  BlackjackListener,
  CommandListSerializer,
  LocalDealer,
  RoundState,
} from 'game-logic';

export class BlackjackGameRunner {
  public get onGameSetupStart$() {
    return this.onGameSetupStart.asObservable();
  }
  public get onGameSetupComplete$() {
    return this.onGameSetupComplete.asObservable();
  }
  public get blackjackLogicActionBus$() {
    return this.roundCreator.blackjackLogicActionBus$;
  }
  public get logicEventQueue$() {
    return this.roundCreator.logicEventQueue$;
  }
  public get currentGame() {
    return this.roundCreator.currentRound?.currentGame;
  }

  private readonly gameListener: BlackjackListener;
  private readonly roundCreator: BlackjackRoundCreator;
  private readonly onGameSetupStart: Subject<void>;
  private readonly onGameSetupComplete: Subject<void>;

  constructor(gameListener: BlackjackListener) {
    this.gameListener = gameListener;
    this.roundCreator = new BlackjackRoundCreator(gameListener);
    this.onGameSetupStart = new Subject();
    this.onGameSetupComplete = new Subject();
  }

  public startNewRemoteRound(logicVersion: number) {
    this.roundCreator.startNewRemoteRound(this.gameListener, logicVersion);

    this.onGameSetupStart.next();
    void this.newGameSetup();
  }

  public startExistingRemoteRound(moves: string[], logicVersion: number) {
    this.roundCreator.startNewRemoteRound(this.gameListener, logicVersion);

    this.onGameSetupStart.next();
    void this.existingGameSetup(moves);
  }

  public startNewLocalRound() {
    this.roundCreator.startNewLocalRound(this.gameListener);

    this.onGameSetupStart.next();
    void this.newGameSetup();
  }

  public clickStartGame() {
    this.gameListener.onStartGame();
  }

  public copyTestCase() {
    const game = this.currentGame;
    if (!game) {
      return;
    }
    if (game.dealer instanceof LocalDealer) {
      navigator.clipboard.writeText(
        JSON.stringify({
          attestation: game.dealer.attestation(),
          moves: game.serializeCommandList(),
        }),
      );
    }
  }

  private async newGameSetup(): Promise<void> {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }

    game.startGame(false);

    this.onGameSetupComplete.next();
    this.roundCreator.currentRound?.onGameSetupAnimationFinished();
  }

  private async existingGameSetup(moves: string[]): Promise<void> {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }

    game.startGame(true);

    const [instantMoves, animatedMoves] = splitAtLastBet(moves);

    await this.executeExistingMoves(game, instantMoves, true);
    await this.executeExistingMoves(game, animatedMoves, false);

    this.onGameSetupComplete.next();
    this.roundCreator.currentRound?.onGameSetupAnimationFinished();
  }

  private async executeExistingMoves(game: BlackjackGameLogic, moves: string[], instant: boolean) {
    for (const move of moves) {
      const command = CommandListSerializer.moveToCommand(move);
      game.executeCommand(command, true, instant);
      await game.waitForAllTasksToBeCompleted();

      if (instant) {
        // Don't auto-continue if we're replaying the last hand
        if (game.blackjackState.getRoundState() === RoundState.Continue) {
          await game.continueRound(instant);
        }
      }
    }
  }
}

function splitAtLastBet(commands: string[]): [string[], string[]] {
  // Find the last occurrence of a command that starts with "bet"
  const lastBetIndex = commands.map((cmd) => cmd.startsWith('bet')).lastIndexOf(true);

  if (lastBetIndex === -1 || lastBetIndex === 0) {
    // If there's no "bet" comman or it's already at the start, return the an empty list and original
    return [[], commands];
  }

  return [commands.slice(0, lastBetIndex), commands.slice(lastBetIndex)];
}
